<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide">
    <q-card class="q-dialog-plugin">
      <q-card-section class="column q-gutter-y-sm">
        <div class="text-bold">Export Penjualan Bulanan</div>
        <div class="row items-center">
          <div style="width: 100px">Tanggal Awal</div>
          <q-input class="l-grow" outlined dense v-model="data.date_from">
            <q-popup-proxy>
              <q-date
                v-model="data.date_from"
                mask="YYYY-MM-DD"
                :options="optionsFrom"
              >
                <div class="row items-center justify-end">
                  <q-btn v-close-popup label="OK" color="primary" flat />
                </div>
              </q-date>
            </q-popup-proxy>
          </q-input>
        </div>
        <div class="row items-center">
          <div style="width: 100px">Tanggal Akhir</div>
          <q-input
            class="l-grow"
            outlined
            dense
            :disable="!data.date_from"
            v-model="data.date_to"
          >
            <q-popup-proxy
              cover
              transition-show="scale"
              transition-hide="scale"
            >
              <q-date
                v-model="data.date_to"
                mask="YYYY-MM-DD"
                :options="optionsTo"
              >
                <div class="row items-center justify-end">
                  <q-btn v-close-popup label="OK" color="primary" flat />
                </div>
              </q-date>
            </q-popup-proxy>
          </q-input>
        </div>
        <div class="text-caption text-grey-8">
          *Hanya scan keluar (retur tidak dihitung). Bulan pertama & terakhir
          mengikuti tanggal yang dipilih.
        </div>
      </q-card-section>
      <q-card-actions align="right">
        <q-btn flat color="brand" label="Cancel" @click="onCancelClick" />
        <q-btn
          color="brand"
          label="Export"
          :loading="data.loading"
          :disable="!data.date_from || !data.date_to"
          @click="exportExcell"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>
import { useDialogPluginComponent } from "quasar";
import useBasic from "@/composables/useBasic";
import * as ExcelJS from "exceljs";
import { saveAs } from "file-saver";

export default {
  props: [],

  emits: [...useDialogPluginComponent.emits],

  setup(props) {
    const { $http, onMounted, reactive, moment, getExcellColumn } = useBasic();

    onMounted(async () => {
      await getDataGap();
    });

    let data = reactive({
      dataGap: {
        min: moment().format("YYYY-MM-DD"),
        max: moment().format("YYYY-MM-DD"),
      },

      date_from: null,
      date_to: null,
      loading: false,
    });

    let getDataGap = async () => {
      let resp = await $http.get(`/dashboard/data-gap`);
      data.dataGap = resp.data;
    };

    const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } =
      useDialogPluginComponent();

    let optionsFrom = (date) => {
      return (
        date <= moment().format("YYYY/MM/DD") &&
        date >= moment(data.dataGap.min).format("YYYY/MM/DD")
      );
    };

    let optionsTo = (date) => {
      return (
        date <= moment().format("YYYY/MM/DD") &&
        date >= moment(data.date_from).format("YYYY/MM/DD")
      );
    };

    // Isi 1 sheet: kolom kunci (Artikel/Warna atau SKU) + 1 kolom per bulan
    // + Total, baris Grand Total di bawah.
    let fillSheet = async (sheet, keyColumns, months, list) => {
      let columns = [...keyColumns];
      let multiYear =
        months[0].year() != months[months.length - 1].year();
      let summaryDown = {};
      for (let m of months) {
        let key = m.format("YYYY-MM");
        columns.push({
          header: `Penjualan ${m.format(multiYear ? "MMM 'YY" : "MMM")}`,
          key: key,
          width: 13,
        });
        summaryDown[key] = 0;
      }
      columns.push({ header: "Total", key: "total", width: 11 });
      sheet.columns = columns;

      summaryDown.total = 0;
      for (let item of list) {
        let row = {};
        for (let col of keyColumns) row[col.key] = item[col.key];
        let total = 0;
        for (let m of months) {
          let key = m.format("YYYY-MM");
          let qty = item.sales[key] || 0;
          row[key] = qty;
          total += qty;
          summaryDown[key] += qty;
        }
        row.total = total;
        summaryDown.total += total;
        sheet.addRow(row);
      }
      sheet.addRow(summaryDown);

      let headerRow = sheet.getRow(1);
      headerRow.font = { bold: true };
      headerRow.alignment = { horizontal: "center", vertical: "middle" };

      let rowKey = list.length + 2;
      let row = sheet.getRow(rowKey);
      let column = sheet.getColumn(await getExcellColumn(columns.length));
      for (let target of [row, column]) {
        target.font = { bold: true };
        target.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "E6E6E6" },
        };
      }
      let lastKey = await getExcellColumn(keyColumns.length);
      if (keyColumns.length > 1) sheet.mergeCells(`A${rowKey}:${lastKey}${rowKey}`);
      sheet.getCell(`A${rowKey}`).value = "Total";
      sheet.getCell(`A${rowKey}`).alignment = { horizontal: "center" };

      sheet.views = [
        { state: "frozen", xSplit: keyColumns.length, ySplit: 1 },
      ];
      sheet.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: rowKey - 1, column: columns.length },
      };
    };

    let exportExcell = async () => {
      data.loading = true;
      try {
        let from = moment(data.date_from).format("YYYY-MM-DD");
        let to = moment(data.date_to).format("YYYY-MM-DD");
        let resp = await $http.get(`/dashboard/export-monthly/${from}/${to}`);

        let months = [];
        let cursor = moment(from).startOf("month");
        while (cursor.isSameOrBefore(moment(to), "month")) {
          months.push(cursor.clone());
          cursor.add(1, "month");
        }

        const workbook = new ExcelJS.Workbook();
        await fillSheet(
          workbook.addWorksheet("Penjualan per Bulan"),
          [
            { header: "Artikel", key: "kode", width: 12 },
            { header: "Warna", key: "warna", width: 12 },
          ],
          months,
          resp.data.rows,
        );
        await fillSheet(
          workbook.addWorksheet("Scan Tidak Valid"),
          [{ header: "SKU (hasil scan)", key: "sku", width: 36 }],
          months,
          resp.data.invalid,
        );

        const name = `Penjualan per Bulan ${moment(from).format(
          "DD MMM 'YY",
        )} - ${moment(to).format("DD MMM 'YY")}.xlsx`;
        const buffer = await workbook.xlsx.writeBuffer();
        const fileType =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        const blob = new Blob([buffer], { type: fileType });
        saveAs(blob, name);
        onDialogOK();
      } finally {
        data.loading = false;
      }
    };

    return {
      data,
      optionsFrom,
      optionsTo,
      dialogRef,
      onDialogHide,

      onCancelClick: onDialogCancel,
      exportExcell,
    };
  },
};
</script>
